/**
 * Market — selling surplus, buying scarcity. Spec §1 defers this layer and says
 * nothing else about it, so the design is derived from what the colony has too
 * much and too little of.
 *
 * CREDITS ARE NOT AN OBJECTIVE. Nothing scores them and nothing else spends
 * them. The market is a way to turn surplus into something useful, and a colony
 * that never opens it is playing the game correctly — measured, trading changes
 * grid completion by nothing at all on a normally generated area, because
 * nothing is actually scarce there.
 *
 * ELEMENTS AND GOODS BOTH TRADE. Elements because sulfur, chlorine and fluorine
 * accumulate with no consumer at all (SPEC-ERRATA E2). Goods because the factory
 * makes seven of them against buildings that want them in fixed proportions, so
 * a run of habitations leaves conductive stock piling up and a run of power
 * plants leaves concreteBlock piling up — surplus that has nothing to do with waste.
 *
 * Orders are STANDING, not one-shot. This is a game played in a background tab;
 * a market that needs babysitting would be a worse mechanic than no market.
 */

import { apportion } from './apportion';
import { sortedHexKeys } from './hex';
import {
  BUY_SPREAD_BP,
  MARKET_DEPTH,
  MARKET_THROUGHPUT,
  MAX_PRICE_BP,
  MIN_PRICE_BP,
  PRICE_REVERSION_BP,
  PRICE_SCALE,
  STARTING_CREDITS,
  WASTE_PRICE,
} from './constants';
import {
  ELEMENT_IDS,
  GOOD_IDS,
  MINERAL_IDS,
  type ElementId,
  type GoodId,
  type MineralId,
} from './data';
import {
  elementsForGoods,
  elementsFromMinerals,
  targetMineralShareBp,
  viableCityElementDemand,
} from './demand';
import type {
  GameState,
  MarketOrder,
  MarketState,
  PlanetState,
  TradeableId,
} from './types';

const BP = 10000;

/** Everything the market moves, in a fixed order. spec §11 rule 3 */
export const TRADEABLE_IDS: readonly TradeableId[] = [...ELEMENT_IDS, ...GOOD_IDS];

export function isGood(id: TradeableId): id is GoodId {
  return (GOOD_IDS as readonly string[]).includes(id);
}

/** How many of this the colony holds. */
function held(state: GameState, id: TradeableId): number {
  return isGood(id)
    ? state.inventory.goods[id]
    : state.inventory.elements[id as ElementId];
}

function adjust(state: GameState, id: TradeableId, delta: number): void {
  if (isGood(id)) state.inventory.goods[id] += delta;
  else state.inventory.elements[id as ElementId] += delta;
}

/**
 * Base prices, derived from DEMAND against SUPPLY.
 *
 * Elements are priced from how badly the colony wants one against how readily
 * this area's ground gives it up. Goods are priced from the elements they
 * embody, plus a margin for the work — a manufactured thing is worth more than
 * the pile of elements it came from, which is the whole reason to manufacture.
 *
 * SCARCITY ALONE IS THE WRONG MODEL for elements, and pricing on it produced an
 * absurd result: fluorine came out the most valuable thing in the game at 2,396
 * a unit, because apatite yields one per unit and apatite is a small share of
 * the mix — while nothing anywhere consumes fluorine. Rarity without demand is
 * not value; it is a smaller pile of rubbish. Elements no recipe consumes are
 * priced as scrap however rare they are.
 */
export function deriveBasePrices(
  worldSupply: Record<ElementId, number>,
): Record<TradeableId, number> {
  const demand = viableCityElementDemand();

  let supplyTotal = 0;
  let demandTotal = 0;
  for (const id of ELEMENT_IDS) {
    supplyTotal += worldSupply[id];
    demandTotal += demand[id];
  }

  const prices = {} as Record<TradeableId, number>;

  for (const id of ELEMENT_IDS) {
    // Waste, or drawn from the air. Scrap value either way.
    if (demand[id] <= 0 || worldSupply[id] <= 0 || supplyTotal <= 0 || demandTotal <= 0) {
      prices[id] = WASTE_PRICE;
      continue;
    }
    const demandShareBp = Math.round((demand[id] * BP) / demandTotal);
    const supplyShareBp = Math.round((worldSupply[id] * BP) / supplyTotal);
    prices[id] = Math.max(
      WASTE_PRICE,
      Math.round((PRICE_SCALE * demandShareBp) / Math.max(1, supplyShareBp)),
    );
  }

  // Goods: what their elements are worth, plus a manufacturing margin. Priced
  // from their own elements rather than independently, so selling a good is
  // always worth more than selling the elements it was made from — otherwise
  // running the factory to sell the output would be a loss.
  for (const id of GOOD_IDS) {
    const elements = elementsForGoods({ [id]: 1 });
    let value = 0;
    for (const element of ELEMENT_IDS) {
      value += elements[element] * prices[element];
    }
    prices[id] = Math.max(WASTE_PRICE, Math.round((value * MANUFACTURING_MARGIN_BP) / BP));
  }

  return prices;
}

/** Goods sell for this much more than the elements inside them. */
const MANUFACTURING_MARGIN_BP = 13000;

/** Elements obtainable from an area, as generated. */
export function areaElementSupply(planet: PlanetState): Record<ElementId, number> {
  const minerals = {} as Record<MineralId, number>;
  for (const id of MINERAL_IDS) minerals[id] = 0;

  for (const key of sortedHexKeys(planet.hexes)) {
    const hex = planet.hexes[key];
    if (hex === undefined) continue;
    for (const id of MINERAL_IDS) minerals[id] += hex.initialDeposits[id];
  }
  return elementsFromMinerals(minerals);
}

/** Prices for a standard mix. Used where no specific area is in hand. */
export const REFERENCE_PRICES: Record<TradeableId, number> = (() => {
  const shares = targetMineralShareBp();
  const mix = {} as Record<MineralId, number>;
  for (const id of MINERAL_IDS) mix[id] = shares[id];
  return deriveBasePrices(elementsFromMinerals(mix));
})();

export function emptyMarket(planet?: PlanetState): MarketState {
  const priceBp = {} as Record<TradeableId, number>;
  const orders = {} as Record<TradeableId, MarketOrder>;
  for (const id of TRADEABLE_IDS) {
    priceBp[id] = BP;
    orders[id] = { mode: 'idle', threshold: 0 };
  }
  const basePrice =
    planet === undefined
      ? { ...REFERENCE_PRICES }
      : deriveBasePrices(areaElementSupply(planet));

  return { credits: STARTING_CREDITS, basePrice, priceBp, orders, lastTraded: {} };
}

/** What one unit sells for right now. */
export function sellPrice(market: MarketState, id: TradeableId): number {
  return Math.max(1, Math.floor((market.basePrice[id] * market.priceBp[id]) / BP));
}

/** What one unit costs to buy. Always above the selling price — see the spread. */
export function buyPrice(market: MarketState, id: TradeableId): number {
  return Math.max(1, Math.floor((sellPrice(market, id) * BUY_SPREAD_BP) / BP));
}

/**
 * Move a price against the direction of trade.
 *
 * Selling pushes down, buying pushes up, in proportion to volume against market
 * depth. Clamped, so nothing can be driven to worthless or to infinity.
 */
function applyImpact(market: MarketState, id: TradeableId, signedUnits: number): void {
  if (signedUnits === 0) return;
  const delta = Math.round((signedUnits * BP) / MARKET_DEPTH);
  market.priceBp[id] = Math.min(
    MAX_PRICE_BP,
    Math.max(MIN_PRICE_BP, market.priceBp[id] + delta),
  );
}

/** Drift every price back toward base. */
function revertPrices(market: MarketState): void {
  for (const id of TRADEABLE_IDS) {
    const current = market.priceBp[id];
    if (current === BP) continue;
    const step = Math.min(PRICE_REVERSION_BP, Math.abs(current - BP));
    market.priceBp[id] = current > BP ? current - step : current + step;
  }
}

function desiredTrades(state: GameState): {
  sells: Partial<Record<TradeableId, number>>;
  buys: Partial<Record<TradeableId, number>>;
} {
  const sells: Partial<Record<TradeableId, number>> = {};
  const buys: Partial<Record<TradeableId, number>> = {};

  for (const id of TRADEABLE_IDS) {
    const order = state.market.orders[id];
    const stock = held(state, id);

    if (order.mode === 'sell') {
      const surplus = stock - order.threshold;
      if (surplus > 0) sells[id] = surplus;
    } else if (order.mode === 'buy') {
      const shortfall = order.threshold - stock;
      if (shortfall > 0) buys[id] = shortfall;
    }
  }

  return { sells, buys };
}

/**
 * Settle this tick's standing orders.
 *
 * Sells run before buys, deliberately: a colony running both should be able to
 * fund its purchases from its surplus in the same tick rather than holding a
 * float. Freighter capacity is split between the two directions rather than
 * served first-come — the freighter runs both ways, and letting one big sell
 * order consume the whole tick meant buying never happened at all.
 */
export function advanceMarket(state: GameState): void {
  const market = state.market;
  market.lastTraded = {};

  const { sells, buys } = desiredTrades(state);

  let sellTotal = 0;
  let buyTotal = 0;
  for (const id of TRADEABLE_IDS) {
    sellTotal += sells[id] ?? 0;
    buyTotal += buys[id] ?? 0;
  }

  const sellBudget = buyTotal > 0 ? Math.floor(MARKET_THROUGHPUT / 2) : MARKET_THROUGHPUT;
  let capacityLeft = MARKET_THROUGHPUT;

  if (sellTotal > 0) {
    const allowed = apportion(
      TRADEABLE_IDS,
      (id) => sells[id] ?? 0,
      Math.min(sellBudget, sellTotal),
      (id) => sells[id] ?? 0,
    );

    for (const id of TRADEABLE_IDS) {
      const units = Math.min(allowed[id], held(state, id));
      if (units <= 0) continue;

      adjust(state, id, -units);
      market.credits += units * sellPrice(market, id);
      applyImpact(market, id, -units);
      market.lastTraded[id] = (market.lastTraded[id] ?? 0) - units;
      capacityLeft -= units;
    }
  }

  if (buyTotal > 0 && capacityLeft > 0) {
    const allowed = apportion(
      TRADEABLE_IDS,
      (id) => buys[id] ?? 0,
      Math.min(capacityLeft, buyTotal),
      (id) => buys[id] ?? 0,
    );

    for (const id of TRADEABLE_IDS) {
      let units = allowed[id];
      if (units <= 0) continue;

      // Never spend credits the colony does not have. Capped by the purse, per
      // commodity, in fixed order, so a shortfall is deterministic rather than
      // depending on which order happened to settle first.
      const price = buyPrice(market, id);
      units = Math.min(units, Math.floor(market.credits / price));
      if (units <= 0) continue;

      adjust(state, id, units);
      market.credits -= units * price;
      applyImpact(market, id, units);
      market.lastTraded[id] = (market.lastTraded[id] ?? 0) + units;
    }
  }

  revertPrices(market);
}

export function setMarketOrder(
  state: GameState,
  id: TradeableId,
  mode: MarketOrder['mode'],
  threshold: number,
): void {
  state.market.orders[id] = { mode, threshold: Math.max(0, Math.floor(threshold)) };
}

